import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { InputStyle, TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatPhoneNumber } from "@sunSource/Common/Utilities/formatPhoneNumber";
import {
    CallbackType,
    ErrorObjectType,
    InputObjectType,
    validate,
    validateConfirmPasswordHandler,
    validatePasswordHandler,
} from "@sunSource/Common/Utilities/validate";
import {
    FormErrorsStateType,
    intialErrorFormState,
    newCustomerValidatorConfig,
    ValidatorConfigType,
} from "@sunSource/Components/CreateAccountNewFormValidatorConfig";
import setOnePageCreateAccountData from "@sunSource/Handlers/NewOnePageCheckout/OnePageCheckoutCreateAccountHandler";
import addAccount from "@sunSource/Store/Accounts/Handlers/AddAccount";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import SetOnePageTabData from "@sunSource/Store/Data/NewOnePageCheckout/Handlers/OnePageCheckoutTabHandler";
import {
    getOnePageCheckoutAccountState,
    getOnePageCheckoutTabState,
} from "@sunSource/Store/Data/NewOnePageCheckout/OnePageCheckoutStateSelectors";
import DeliveryMethod from "@sunSource/Widgets/NewOnePageCheckout/DeliveryMethod";
import flatMap from "lodash/flatMap";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks, useSelector } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import ShipToSelectorModal, { ShipToSelectorModalStyles } from "@insite/content-library/Widgets/OnePageCheckout/ShipToSelectorModal";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

type Props =  ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> &
   ThemeProps<BaseTheme>;

interface MessageFilterStyles {
    radioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioButton?: FieldSetPresentationProps<RadioComponentProps>;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading: isCartLoading } = cartState;
   
    return {
        cart,
        accountSettings: getSettingsCollection(state).accountSettings,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        tabState: getOnePageCheckoutTabState(state),
        countries: getCurrentCountries(state),
        createAccountData: getOnePageCheckoutAccountState(state),
        userShipToData: state.data?.shipTos,
        userShipToId: state.context.session.shipToId ?? "",
        isAuthenticated: state.context.session.isAuthenticated,
        shipToState: getShipToState(state, cart?.shipToId),
    };
};

const mapDispatchToProps = {
    signIn,
    SetOnePageTabData,
    setOnePageCreateAccountData,
    setAddressBookOverlayIsOpen
};

export interface CreateAccountNewFormStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    fieldsGridItem?: GridItemProps;
    fieldsGridItemCity?: GridItemProps;
    fieldsGridItemZip?: GridItemProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    firstNameGridItem?: GridItemProps;
    jobTitleGridItem?: GridItemProps;
    companyNameGridItem?: GridItemProps;
    addressLineOneGridItem?: GridItemProps;
    addressLineTwoGridItem?: GridItemProps;
    cityGridItem?: GridItemProps;
    zipCodeGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    firstNameTextField?: TextFieldPresentationProps;
    jobTitleTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    companyNameTextField?: TextFieldPresentationProps;
    cityTextField?: TextFieldPresentationProps;
    zipCodeTextField?: TextFieldPresentationProps;
    addressLineOneTextField?: TextFieldPresentationProps;
    addressLineTwoTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    stateGridItem?: GridItemProps;
    businessTypeGridItem?: GridItemProps;
    stateTextField?: SelectPresentationProps;
    countryGridItem?: GridItemProps;
    countryTextField?: SelectPresentationProps;
    phoneNumberGridItem?: GridItemProps;
    faxGridItem?: GridItemProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    faxTextField?: TextFieldPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    companyTypeText?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    fieldSetParentDiv?: InjectableCss;
    messageFilter?: MessageFilterStyles;
    requiredFieldDiv?: TypographyProps;
    shipToBillToCheck?: InjectableCss;
    passwordValidationMessage?: InjectableCss;
    loginBtn?: ButtonPresentationProps;
    lostYourPasswordSection?: GridItemProps;
    loginButtonSection?: GridItemProps;
    lostYourPassword?: ClickablePresentationProps;
    companyDetailsWrapper?: InjectableCss;
    companyName?: InjectableCss;
    contactInfo?: InjectableCss;
    changeLabel?: InjectableCss;
    companyDetailsGridItem?: GridItemProps;
    inputHeader?: InjectableCss;
    shipToSelectorModal?: ShipToSelectorModalStyles;
    shipToGridItem?: GridItemProps;    
    addressInfoDisplay?: AddressInfoDisplayStyles;
}

export const createAccountNewFormStyles: CreateAccountNewFormStyles = {
    requiredFieldDiv: {
        css: css`
            > ${TypographyStyle} {
                margin-top: 15px;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    mainGridContainer: {
        css: css`
            padding: 0px 15px;
            margin-top: 20px;
            margin-bottom: 15px;
            padding-top: 15px;
            [class^="GridItemStyle"] {
                padding-bottom: 5px;
                margin-bottom: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            [class^="GridOffset"] {
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            .containerTitle {
                width: 100%;
                display: flex;
                margin: 0px 15px;
                margin-bottom: 10px !important;
                > ${TypographyStyle} {
                    &:first-child {
                        font-weight: bold;
                        font-size: 18px;
                    }
                }
                [class^="CheckboxGroupStyle"] {
                    margin-left: 30px;
                }
            }
            [class^="FormInputWrapper"] {
                margin-top: 0 !important;
                > ${InputStyle} {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
                > select[class^="SelectStyle"] {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
            }
        `,
    },
    businessTypeGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            > div[class^="RadioGroupStyle"] {
                justify-content: flex-start;
                display: inline-block;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
                > div[class^="RadioStyle"] {
                    margin-top: 0;
                    margin-right: 25px;
                    display: inline-block;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 50%;
                                margin-right: 0;
                            `,
                            css`
                                width: 50%;
                                margin-right: 0;
                            `,
                            null,
                            null,
                            null,
                        ])}
                    &:last-child {
                        margin-right: 0;
                    }
                    > label[class^="TypographyStyle"] {
                        font-size: 13px;
                    }
                }
            }
        `,
    },
    fieldsGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    fieldsGridItemCity: {
        width: [12, 12, 12, 4, 4],
    },
    fieldsGridItemZip: {
        width: [12, 12, 12, 4, 4],
    },
    countryGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    stateGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            select {
                height: 39.98px;
            }
            div {
                width: 100% !important;
            }
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    select {
                        height: 39.98px;
                    }
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    subscriptionGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding: 0px 0px 0px 15px;
        `,
    },
    submitErrorGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: end;
            padding: 0px 0px 35px 0px;
        `,
    },
    emailTextField: {
        cssOverrides: {
            inputSelect: css`
                font-size: 16px;
                padding: 0 2rem 0 10px !important;
                &:focus {
                    padding: 0 2rem 0 10px !important;
                }
            `,
        },
    },
    fieldSetParentDiv: {
        css: css`
            display: inline-block;
            vertical-align: top;
            > ${RadioStyle} {
                white-space: nowrap;
            }
        `,
    },
    shipToBillToCheck: {
        css: css`
            margin-top: 15px;
            margin-left: 15px;
            width: 100%;
        `,
    },
    companyTypeText: {
        size: 13,
        weight: "bold",
    },
    passwordValidationMessage: {
        css: css`
            ul {
                list-style: initial;
                padding-left: 20px;
                font-size: 14px;
            }
        `,
    },

    loginBtn: {
        variant: "tertiary",
        css: css`
            width: 120px;
            text-transform: uppercase;
            > ${TypographyStyle} {
                font-weight: 400;
            }
        `,
    },
    lostYourPasswordSection: {
        width: [12, 12, 6, 6, 6],
        css: css``,
    },
    lostYourPassword: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    loginButtonSection: {
        width: [12, 12, 12, 12, 12],
        css: css`
            justify-content: right;
        `,
    },
    companyDetailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    companyName: {
        css: css`
            font-weight: 600;
        `,
    },
    contactInfo: {
        css: css`
            color: gray;
        `,
    },
    changeLabel: {
        css: css`
            color: #00539b;
            font-weight: 600;
            cursor: pointer;
        `,
    },
    companyDetailsGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            padding-top: 0px;
        `,
    },
    inputHeader: {
        css: css`
            margin-bottom: 10px;
            display: block;
            font-size: 18px !important;
        `,
    },
    shipToGridItem: { width: 12, css:css`
            padding: 0px;
            padding-top: 5px;
            [data-test-selector="addressInfoDisplay_companyName"]{
            font-weight:600;
            }
            p{
                color: gray;
                text-transform: capitalize;
            }
            ` },
};

const styles = createAccountNewFormStyles;

const StyledForm = getStyledWrapper("form");

const ShippingAddressAndDeliveryMethod = ({
    accountSettings,
    signInUrl,
    signIn,
    tabState,
    createAccountData,
    SetOnePageTabData,
    countries,
    setOnePageCreateAccountData,
    userShipToData,
    userShipToId,
    isAuthenticated,
    cart,
    theme,
    setAddressBookOverlayIsOpen,
    shipToState
}: Props) => {
    const firstNameRequiredFieldMessage = siteMessage("First name is Required");
    const lastNameRequiredFieldMessage = siteMessage("Last name is Required");
    const companyNameRequiredFieldMessage = siteMessage("Company name is Required");
    const cityRequiredFieldMessage = siteMessage("City is Required");
    const billToCityRequiredFieldMessage = siteMessage("City is Required");
    const zipCodeRequiredFieldMessage = siteMessage("Zip Code is Required");
    const billToZipCodeRequiredFieldMessage = siteMessage("Zip Code is Required");
    const addressLineOneRequiredFieldMessage = siteMessage("Address Line One is Required");
    const billToAddressLineOneRequiredFieldMessage = siteMessage("Address Line One is Required");
    const addressLineTwoRequiredFieldMessage = siteMessage("Address Line Two is Required");
    const billToAddressLineTwoRequiredFieldMessage = siteMessage("Address Line Two is Required");
    const phoneNumberRequiredFieldMessage = siteMessage("Phone number is Required");
    const faxRequiredFieldMessage = siteMessage("Fax is Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const countryRequiredFieldMessage = siteMessage("Country is Required");
    const billToCountryRequiredFieldMessage = siteMessage("Country is Required");
    const stateRequiredFieldMessage = siteMessage("State is Required");
    const billToStateRequiredFieldMessage = siteMessage("State is Required");
    const jobTitleRequiredFieldMessage = siteMessage("Job Title is Required");

    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormState);
    const [changeShippingClicked, setChangeShippingClicked] = useState(false);

    const [city, setCity] = useState(createAccountData?.city);
    const [billToCity, setBillToCity] = useState("");
    const [zipCode, setZipCode] = useState(createAccountData?.zipCode);
    const [billToZipCode, setBillToZipCode] = useState("");
    const [addressLineOne, setAddressLineOne] = useState(createAccountData?.addressLineOne);
    const [billToAddressLineOne, setBillToAddressLineOne] = useState("");
    const [addressLineTwo, setAddressLineTwo] = useState(createAccountData?.addressLineTwo);
    const [billToAddressLineTwo, setBillToAddressLineTwo] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [fax, setFax] = useState("");
    const [email, setEmail] = useState("");
    const [selectedState, setSelectedState] = useState<StateModel | undefined>(undefined);
    const [billToSelectedState, setBillToSelectedState] = useState<StateModel | undefined>(undefined);
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [errorMessage, setErrorMessage] = useState("");
    // const [isSubscribed, setIsSubscribed] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormSubmitted, setFormSubmitted] = React.useState(false);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
    const [password, setPassword] = React.useState("");
    const [confirmPassword, setConfirmPassword] = React.useState("");
    const [showPassword, setShowPassword] = React.useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState<React.ReactNode>("");
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState<React.ReactNode>("");
    const [businessType, setBusinessType] = React.useState("OEM (Original Equipment Manufacturer)");
    const [IsShipToSameAsBilling, setIsShipToSameAsBillTo] = React.useState(true);

    const selCountry = countries?.find(option => option.name === "United States");
    const [firstNameError, setFirstNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : firstNameRequiredFieldMessage,
    );

    const [billToSelectedCountry, setBillToSelectedCountry] = useState<CountryModel | undefined>(selCountry);
    const [jobTitleError, setJobTitleError] = useState(
        accountSettings?.useEmailAsUserName ? "" : jobTitleRequiredFieldMessage,
    );

    const [lastNameError, setLastNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : lastNameRequiredFieldMessage,
    );

    const [companyNameError, setCompanyNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : companyNameRequiredFieldMessage,
    );

    const [cityError, setCityError] = useState(accountSettings?.useEmailAsUserName ? "" : cityRequiredFieldMessage);

    const [billToCityError, setBillToCityError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToCityRequiredFieldMessage,
    );

    const [zipCodeError, setZipCodeError] = useState(
        accountSettings?.useEmailAsUserName ? "" : zipCodeRequiredFieldMessage,
    );

    const [billToZipCodeError, setBillToZipCodeError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToZipCodeRequiredFieldMessage,
    );

    const [addressLineOneError, setAddressLineOneError] = useState(
        accountSettings?.useEmailAsUserName ? "" : addressLineOneRequiredFieldMessage,
    );

    const [billToAddressLineOneError, setBillToAddressLineOneError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToAddressLineOneRequiredFieldMessage,
    );

    const [addressLineTwoError, setAddressLineTwoError] = useState(
        accountSettings?.useEmailAsUserName ? "" : addressLineTwoRequiredFieldMessage,
    );

    const [billToAddressLineTwoError, setBillToAddressLineTwoError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToAddressLineTwoRequiredFieldMessage,
    );

    const [phoneNumberError, setPhoneNumberError] = useState(
        accountSettings?.useEmailAsUserName ? "" : phoneNumberRequiredFieldMessage,
    );

    const [faxError, setFaxError] = useState(accountSettings?.useEmailAsUserName ? "" : faxRequiredFieldMessage);

    const [countryError, setCountryError] = useState(
        accountSettings?.useEmailAsUserName ? "" : countryRequiredFieldMessage,
    );

    const [billToCountryError, setBillToCountryError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToCountryRequiredFieldMessage,
    );

    const [stateError, setStateError] = useState(accountSettings?.useEmailAsUserName ? "" : stateRequiredFieldMessage);

    const [billToStateError, setBillToStateError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToStateRequiredFieldMessage,
    );

    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(selCountry);
    React.useEffect(() => {
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    }, [formErrors]);

    /*  React.useEffect(() => {
        if (selCountry) {
            setSelectedCountry(selCountry);
        }
        setCountryError("");
    }, [countries]);

    React.useEffect(() => {
        if (selCountry) {
            setBillToSelectedCountry(selCountry);
        }
        setBillToCountryError("");
    }, [countries]); */

    /*   React.useEffect(() => {
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }, []); */

    React.useEffect(() => {
        setShippingAddressSameAsBillingAddress(false);
        const cloneFormErrors = { ...formErrors };
        for (const [key] of Object.entries(cloneFormErrors)) {
            const element: HTMLSelectElement | HTMLInputElement | null = document.querySelector(`[name="${key}"]`);
            if (element && element.value) {
                delete cloneFormErrors[key as keyof FormErrorsStateType];
                setFormErrors(removeKey(cloneFormErrors, key));
            }
        }
    }, []);

    const resetForm = () => {
        setFirstNameError("");
        setJobTitleError("");
        setLastNameError("");
        setCompanyNameError("");
        setCity("");
        setCityError("");
        setBillToCity("");
        setBillToCityError("");
        setZipCode("");
        setZipCodeError("");
        setBillToZipCode("");
        setBillToZipCodeError("");
        setAddressLineOne("");
        setAddressLineOneError("");
        setBillToAddressLineOne("");
        setBillToAddressLineOneError("");
        setAddressLineTwo("");
        setAddressLineTwoError("");
        setBillToAddressLineTwo("");
        setBillToAddressLineTwoError("");
        setPhoneNumber("");
        setPhoneNumberError("");
        setFax("");
        setFaxError("");
        setEmail("");
        setEmailError("");
        setCountryError("");
        setBillToCountryError("");
        setSelectedState(undefined);
        setStateError("");
        setBillToSelectedState(undefined);
        setBillToStateError("");
        setPassword("");
        setConfirmPassword("");
        setPasswordErrorMessage("");
        setConfirmPasswordErrorMessage("");
    };

    const validateSubmitDisabled = (errorsState: FormErrorsStateType) => {
        return Object.keys(errorsState).length !== 0;
    };

    const removeKey = (formErrorObject: FormErrorsStateType, deleteKey: string) => {
        return Object.assign(
            {},
            ...Object.entries(formErrorObject)
                .filter(([k]) => k !== deleteKey)
                .map(([k, v]) => ({ [k]: v })),
        );
    };

    const validateHandler = (input: HTMLInputElement | HTMLSelectElement, customValidation?: CallbackType): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = newCustomerValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(
            emailError ||
            firstNameError ||
            jobTitleError ||
            lastNameError ||
            companyNameError ||
            cityError ||
            billToCityError ||
            zipCodeError ||
            billToZipCodeError ||
            addressLineOneError ||
            billToAddressLineOneError ||
            addressLineTwoError ||
            billToAddressLineTwoError ||
            phoneNumberError ||
            faxError ||
            countryError ||
            billToCountryError ||
            stateError ||
            billToStateError ||
            passwordErrorMessage ||
            confirmPasswordErrorMessage
        );
    };

    const formatZipCode = (zipCode: string) => {
        const newZipCode = zipCode.replace(/[^\d]/g, "");
        return newZipCode;
    };

    const setShippingAddressSameAsBillingAddress = (isChecked: boolean) => {
        setIsShipToSameAsBillTo(isChecked);
        const shippingErrors = ["addressLineOne", "city", "state", "zipCode"];
        if (isChecked) {
            setAddressLineOne(billToAddressLineOne);
            setAddressLineTwo(billToAddressLineTwo);
            setCity(billToCity);
            setSelectedState(billToSelectedState);
            setZipCode(billToZipCode);
            shippingErrors.forEach(inputName => {
                delete formErrors[inputName as keyof FormErrorsStateType];
                setFormErrors(formErrors);
            });
        } else {
            setAddressLineOne("");
            setAddressLineTwo("");
            setCity("");
            setSelectedState(undefined);
            setZipCode("");
            const selectStateEl: HTMLSelectElement | null = document.querySelector('[name="state"]');
            if (selectStateEl) {
                validateHandler(selectStateEl);
            }
            shippingErrors.forEach(inputName => {
                if (!(inputName === "state" || inputName === "country")) {
                    setFormErrors(prevState => {
                        return {
                            ...prevState,
                            [inputName]: "",
                        };
                    });
                }
            });
        }
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const cityChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setCity(input.value);
    };

    const addressLineOneChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setAddressLineOne(input.value);
    };

    const addressLineTwoChangeHandler = (input: HTMLInputElement) => {
        setAddressLineTwo(input.value);
    };

    const emailChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setEmail(input.value);
    };

    const phoneNumberChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPhoneNumber(formatPhoneNumber(input.value));
    };

    const faxChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setFax(formatPhoneNumber(input.value));
    };

    const zipCodeChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setZipCode(formatZipCode(input.value));
    };

    const billToAddressLineOneChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setBillToAddressLineOne(input.value);
        if (IsShipToSameAsBilling) {
            setAddressLineOne(input.value);
        }
    };

    const billToAddressLineTwoChangeHandler = (input: HTMLInputElement) => {
        setBillToAddressLineTwo(input.value);
        if (IsShipToSameAsBilling) {
            setAddressLineTwo(input.value);
        }
    };

    const billToCityChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setBillToCity(input.value);
        if (IsShipToSameAsBilling) {
            setCity(input.value);
        }
    };

    const handleBillToCountryChange: SelectProps["onChange"] = event => {
        const country = countries?.find(c => c.id === event.target.value);
        setBillToSelectedCountry(country);
        if (IsShipToSameAsBilling) {
            setSelectedCountry(country);
        }
    };

    const handleBillToStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        validateHandler(event.target);
        setBillToSelectedState(state);
        if (IsShipToSameAsBilling) {
            setSelectedState(state);
        }
    };

    const billToZipCodeChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setBillToZipCode(formatZipCode(input.value));
        if (IsShipToSameAsBilling) {
            setZipCode(formatZipCode(input.value));
        }
    };

    const businessTypeChangeHandler = (businessType: string) => {
        setBusinessType(businessType);
    };

    const passwordChangeHandler = (input: HTMLInputElement) => {
        validateConfirmPassword(input.value, confirmPassword);
        validatePassword({
            password: input.value,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
        setPassword(input.value);
        pwdHandler(input);
    };

    const confirmPasswordChangeHandler = (input: HTMLInputElement) => {
        setConfirmPassword(input.value);
        validateConfirmPassword(password, input.value);
        pwdHandler(input);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        setIsButtonDisabled(true);
        toasterContext.addToast({
            body: siteMessage("Your account has been successfully created"),
            messageType: "success",
            timeoutLength: 2500,
        });
        function getDataLayer() {
            return (window as any).dataLayer;
        }
        const dataLayer = getDataLayer();
        if (dataLayer) {
            dataLayer.push({
                event: "sign_up",
                sign_up_type: "new",
            });
        }

        // setTimeout(() => {
        //     signIn({
        //         userName: email,
        //         password,
        //         rememberMe: false,
        //         returnUrl,
        //     });
        // }, 500);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            setIsButtonDisabled(false);
            return;
        }

        if (!isSubmitted) {
            setIsSubmitted(true);
        }
    };

    const pwdHandler = (input: HTMLInputElement) => {
        const pwd = input.name === "password" ? input.value : password;
        const confirmPwd = input.name === "confirmPassword" ? input.value : confirmPassword;

        const isValidate: ErrorObjectType | undefined =
            input.name === "password"
                ? validatePasswordHandler(pwd, confirmPwd)
                : validateConfirmPasswordHandler(pwd, confirmPwd);
        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries?.find(c => c.id === event.target.value);
        setSelectedCountry(country);
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.abbreviation === event.target.value);
        validateHandler(event.target);
        setSelectedState(state);
    };

    const componentStyles: MessageFilterStyles = styles.messageFilter || {};

    const loginHandler = () => {
        setOnePageCreateAccountData({
            addressLineOne,
            addressLineTwo,
            city,
            state: selectedState?.abbreviation,
            zipCode,
        });
        completeAction(2);
    };

    
    const [isShipToSelectorModalOpen, setIsShipToSelectorModalOpen] = useState(false);

    const editShippingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsShipToSelectorModalOpen(true);
    };

    const shipToSelectorModalCloseHandler = () => {
        setIsShipToSelectorModalOpen(false);
    };

    const shipTo = shipToState.value;

    const [openTabs, setOpenTabs] = useState<number[]>(tabState.openTabs);
    const [completedTabs, setCompletedTabs] = useState<number[]>(tabState.completedTabs);
    const [currentTab, setCurrentTab] = useState<number>(tabState.currentTab);

    const completeAction = (index: number) => {
        setCurrentTab(index + 1);
        if (!completedTabs.includes(index)) {
            setCompletedTabs([...completedTabs, index]);
            setOpenTabs(prev => [...prev, index + 1]);
        }
    };

    const isInContentAdmin = () => {
        try {
            const parentUrl = window?.parent?.location?.toString().toLowerCase();
            return parentUrl.includes("/contentadmin/");
        } catch (error) {
            // Catch cross-origin errors (if parent URL can't be accessed)
            return false;
        }
    };
    React.useEffect(() => {
        if (isInContentAdmin()) {
            completeAction(2);
        }
    }, []);

    React.useEffect(() => {
        SetOnePageTabData({
            openTabs,
            completedTabs,
            currentTab,
        });
    }, [openTabs, completedTabs, currentTab, SetOnePageTabData]);

    useEffect(() => {
        if (userShipToData.byId[userShipToId] && userShipToId) {
            const { state, address1, address2, city, postalCode } = userShipToData.byId[userShipToId];
            setZipCode(postalCode);
            if (state) {
                setSelectedState(state);
            }
            setCity(city);
            setAddressLineOne(address1);
            setAddressLineTwo(address2);
        }
    }, [userShipToData, userShipToId]);

    useEffect(() => {
        if (addressLineOne && zipCode && city && selectedState && createAccountData.tempoData?.savedService) {
            setOnePageCreateAccountData({
                addressLineOne,
                addressLineTwo,
                city,
                state: selectedState?.abbreviation,
                zipCode,
                billToAddressLineOne: addressLineOne,
                billToAddressLineTwo: addressLineTwo,
                billToCity: city,
                billToState: selectedState?.abbreviation,
                billToZipCode: zipCode,
            });
            completeAction(2);
        }
    }, [
        addressLineOne,
        addressLineTwo,
        zipCode,
        city,
        selectedState,
        createAccountData?.tempoData?.orderNotes,
        createAccountData?.tempoData?.savedService,
    ]);

    const [deliveryMethod, setDeliveryMethod] = useState("");
    const [changeDeliveryMethodClicked, setChangeDeliveryMethodClicked] = useState(false);
     // const editedCartNotes = useSelector(getEditedNotes);
    
        const cartId = useSelector((state: ApplicationState) => state.pages.checkoutShipping.cartId);
    
        // const carter = cartId
        //     ? useSelector((state: any) => getCartState(state, cartId).value)
        //     : useSelector(getCurrentCartState).value;
    
        const carter = cartId
            ? useSelector((state: ApplicationState) => getCartState(state, cartId).value)
            : useSelector((state: ApplicationState) => getCurrentCartState(state).value);
    
        const currentNotes = carter?.notes;

    const [notes, setLocalNotes] = useState("");
    useEffect(() => {
            if (createAccountData.tempoData?.orderNotes) {
                 setLocalNotes(createAccountData.tempoData?.orderNotes);
            }
        }, [currentNotes,createAccountData.tempoData?.orderNotes]);

    useEffect(() => {
        if (isAuthenticated) {
            completeAction(2);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            const savedService = localStorage.getItem("serviceValue");

            flatMap(
                cart?.carriers,
                carrier => carrier.shipVias?.map((shipVia:any) => ({ ...shipVia, carrier })),
                // eslint-disable-next-line array-callback-return
            ).map(shipVia => {
             
                
                if (shipVia?.id === cart?.shipVia?.id) {
                   setDeliveryMethod(shipVia?.description);
                }
            });
        }
    }, [isAuthenticated, cart?.carriers]);

    if (isAuthenticated) {
        return (
            <StyledWrapper>
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.companyDetailsGridItem}>
                        <StyledWrapper {...styles.companyDetailsWrapper}>
                            <Typography {...styles.companyName}>
                                  {translate("Shipping Address ")}
                                <Typography
                                    onClick={() => {
                                        editShippingAddressClickHandler()
                                        
                                    }}
                                    {...styles.changeLabel}
                                >
                                    {translate("(Change)")}
                                </Typography>
                            </Typography>
                            
                            {shipTo &&
                                <GridItem {...styles.shipToGridItem} data-test-selector="ShippingAddress">
                                    <AddressInfoDisplay
                                        {...shipTo}
                                        state={shipTo.state ? shipTo.state.abbreviation : undefined}
                                        country={shipTo.country ? shipTo.country.abbreviation : undefined}
                                        extendedStyles={styles.addressInfoDisplay}
                                    />
                                </GridItem>
                            }
                            <ShipToSelectorModal
                                isOpen={isShipToSelectorModalOpen}
                                onModalClose={shipToSelectorModalCloseHandler}
                                extendedStyles={styles.shipToSelectorModal}
                            />
                        </StyledWrapper>
                        
                    </GridItem>
                        <GridItem width={[12, 12, 12, 6, 6]} style={{paddingTop:"0px"}}>
                            <StyledWrapper {...styles.companyDetailsWrapper}>
                                <Typography {...styles.companyName}>
                                    {translate("Delivery Method")}
                                    {!createAccountData.tempoData?.editDeliveryMethod && 
                                        <Typography
                                            onClick={() => {
                                            setOnePageCreateAccountData({
                                                ...createAccountData,
                                                tempoData: { ...createAccountData.tempoData, editDeliveryMethod: !createAccountData.tempoData?.editDeliveryMethod },
                                            });
                                            }}
                                            {...styles.changeLabel}
                                        >
                                            {" "}{translate("(Change)")}
                                        </Typography>
                                    }   

                                </Typography>
                                <Typography style={{color:"gray"}}>
                                    {deliveryMethod}
                                </Typography>
                                <Typography>
                                    {translate("Notes:")}
                                </Typography>
                                <Typography style={{color:"gray"}}>
                                    {notes}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                    
                </GridContainer>
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper>
            <LoadingOverlay loading={isSubmitted} spinnerProps={{ color: "primary" }}>
                <GridContainer {...styles.mainGridContainer}>
                    <Typography {...styles.inputHeader}>{translate("Shipping Address")}</Typography>
                    <GridContainer className="shipToAddressDiv">
                        <GridItem {...styles.fieldsGridItem}>
                            <TextField
                                {...styles.addressLineOneTextField}
                                name="addressLineOne"
                                value={addressLineOne}
                                data-test-selector="createAccount_addressLineOne"
                                onChange={e => addressLineOneChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.addressLineOne && formErrors.addressLineOne}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.addressLineOne ? AlertTriangle : undefined,
                                }}
                                placeholder="Address Line 1 *"
                                disabled={IsShipToSameAsBilling}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.fieldsGridItem}>
                            <TextField
                                {...styles.addressLineTwoTextField}
                                name="addressLineTwo"
                                value={addressLineTwo}
                                data-test-selector="createAccount_addressLineTwo"
                                onChange={e => addressLineTwoChangeHandler(e.currentTarget)}
                                error={!!formErrors.addressLineTwo && formErrors.addressLineTwo}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.addressLineTwo ? AlertTriangle : undefined,
                                }}
                                placeholder="Address Line 2 (Optional)"
                                disabled={IsShipToSameAsBilling}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldsGridItemCity}>
                            <TextField
                                {...styles.cityTextField}
                                name="city"
                                value={city}
                                data-test-selector="createAccount_city"
                                onChange={e => cityChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.city && formErrors.city}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.city ? AlertTriangle : undefined,
                                }}
                                placeholder="City *"
                                disabled={IsShipToSameAsBilling}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.stateGridItem}>
                            <Select
                                {...styles.stateTextField}
                                name="state"
                                value={selectedState?.abbreviation || ""}
                                onChange={handleStateChange}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.state && formErrors.state}
                                disabled={IsShipToSameAsBilling}
                                required
                            >
                                <option value="" style={{ color: "gray" }}>
                                    {translate("State/Province *")}
                                </option>
                                {selectedCountry?.states?.map(st => (
                                    <option key={st.id} value={st.abbreviation}>
                                        {st.name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fieldsGridItemZip}>
                            <TextField
                                {...styles.zipCodeTextField}
                                name="zipCode"
                                value={zipCode}
                                maxLength={6}
                                data-test-selector="createAccount_zipCode"
                                onChange={e => zipCodeChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.zipCode && formErrors.zipCode}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.zipCode ? AlertTriangle : undefined,
                                }}
                                placeholder="ZIP / Postal Code *"
                                disabled={IsShipToSameAsBilling}
                                required
                            />
                        </GridItem>
                    </GridContainer>
                </GridContainer>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(ShippingAddressAndDeliveryMethod));
